import { useState } from "react";
import { apiFetch, setUserCache } from "../../../lib/api/client";
import { PassphraseInput } from "./PassphraseInput";
import { PassphraseGuide } from "./PassphraseGuide";
import { RecoveryKeyModal } from "./RecoveryKeyModal";
import { Logo } from "../../../ui/icons";

const MIN_WORDS = 4;

interface ResetResponse {
  id: string;
  role?: string;
  recoveryKey: string;
  dataPreserved: boolean;
}

interface ResetPasswordPageProps {
  readonly token: string;
  readonly onSuccess: () => void;
}

export function ResetPasswordPage({ token, onSuccess }: ResetPasswordPageProps) {
  const [userRecoveryKey, setUserRecoveryKey] = useState("");
  const [words, setWords] = useState<string[]>([]);
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"passphrase" | "classic">("passphrase");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryKey, setRecoveryKey] = useState<string | null>(null);
  const [showDestructiveWarning, setShowDestructiveWarning] = useState(false);

  const isValid =
    mode === "passphrase"
      ? words.length >= MIN_WORDS
      : password.length >= 8;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    if (!userRecoveryKey.trim() && !showDestructiveWarning) {
      setShowDestructiveWarning(true);
      return;
    }

    const finalPassword = mode === "passphrase" ? words.join(" ") : password;
    setLoading(true);
    setError(null);

    try {
      const body: Record<string, string> = { token, password: finalPassword };
      if (userRecoveryKey.trim()) {
        body.recoveryKey = userRecoveryKey.trim();
      }

      const response = await apiFetch<ResetResponse>("/users/reset-with-token", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setUserCache(response.id, response.role);
      setRecoveryKey(response.recoveryKey);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 400) {
        const msg = (err as Error).message;
        if (msg.includes("recuperation")) {
          setError("Clé de récupération invalide. Vérifiez et réessayez.");
        } else if (msg.includes("courant")) {
          setError("Ce mot de passe est trop courant.");
        } else {
          setError("Ce lien est expiré ou invalide.");
        }
      } else {
        setError("Une erreur est survenue.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (recoveryKey) {
    return <RecoveryKeyModal recoveryKey={recoveryKey} onAcknowledge={onSuccess} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface p-8">
      <div className="w-full max-w-sm flex flex-col gap-8">
        <div className="text-center flex flex-col gap-1">
          <div className="flex items-center justify-center gap-2">
            <Logo className="size-8" />
            <h1 className="text-3xl font-bold text-ink">Palnia</h1>
          </div>
          <p className="text-sm text-ink-muted">Réinitialisation du mot de passe</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {/* Recovery key field */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-muted">
              Clé de récupération (recommandé)
            </label>
            <input
              type="text"
              value={userRecoveryKey}
              onChange={(e) => { setUserRecoveryKey(e.target.value); setShowDestructiveWarning(false); }}
              placeholder="XXXX-XXXX-XXXX-XXXX-..."
              autoComplete="off"
              className="px-4 py-3 bg-surface-alt border border-border text-ink font-mono
                placeholder:text-ink-muted placeholder:font-sans focus:outline-none
                focus:ring-2 focus:ring-accent rounded"
            />
            {userRecoveryKey.trim() ? (
              <p className="text-[11px] text-green-400 leading-relaxed">
                Vos données chiffrées seront conservées.
              </p>
            ) : (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-xs text-red-400 font-medium leading-relaxed">
                  Sans votre clé de récupération, toutes vos données chiffrées (tâches, événements, habitudes) seront perdues définitivement.
                </p>
              </div>
            )}
          </div>

          {/* New password */}
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => { setMode("passphrase"); setPassword(""); }}
              className={`flex-1 px-3 py-2 text-xs transition-colors ${
                mode === "passphrase"
                  ? "bg-accent text-white"
                  : "bg-surface-alt text-ink-muted hover:text-ink"
              }`}
            >
              Phrase de passe (recommandé)
            </button>
            <button
              type="button"
              onClick={() => { setMode("classic"); setWords([]); }}
              className={`flex-1 px-3 py-2 text-xs transition-colors ${
                mode === "classic"
                  ? "bg-accent text-white"
                  : "bg-surface-alt text-ink-muted hover:text-ink"
              }`}
            >
              Mot de passe classique
            </button>
          </div>

          {mode === "passphrase" ? (
            <>
              <PassphraseInput
                words={words}
                onChange={setWords}
                minWords={MIN_WORDS}
                placeholder="Tapez un mot puis Espace..."
                autoComplete="new-password"
              />
              <PassphraseGuide wordCount={words.length} onGenerate={setWords} />
            </>
          ) : (
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Nouveau mot de passe (min 8 caractères)"
              autoComplete="new-password"
              className="px-4 py-3 bg-surface-alt border border-border text-ink
                placeholder:text-ink-muted focus:outline-none focus:ring-2
                focus:ring-accent rounded"
            />
          )}

          <button
            type="submit"
            disabled={loading || !isValid}
            className="mt-1 px-4 py-3 bg-accent text-white font-semibold
              hover:opacity-80 disabled:opacity-40 focus:outline-none
              focus:ring-2 focus:ring-accent rounded transition-opacity"
          >
            {loading ? "Chargement..." : !userRecoveryKey.trim() && showDestructiveWarning
              ? "Confirmer la perte de données"
              : "Réinitialiser mon mot de passe"}
          </button>

          {error && (
            <p className="text-xs text-center text-red-500">{error}</p>
          )}
        </form>
      </div>
    </div>
  );
}
